import { useTheme } from '@mui/material';
import { PercentageChartData } from '@schooly/api';
import type { BarSeriesOption, EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import { FC, useMemo } from 'react';

import { getScaleFactor, getZeroValueHorizontalBarColor } from './utils';

const BAR_TYPE = 'bar';
const PERCENTAGE_MAX_VALUE = 100;

type SeriesData = Pick<PercentageChartData, 'key' | 'value'> &
  Pick<BarSeriesOption, 'itemStyle' | 'emphasis'>;

export interface PercentageChartProps {
  chartData: PercentageChartData[];
  onBarOver?: (id: string) => void;
  onBarOut?: () => void;
  gridOptions?: EChartsOption['grid'];
  barOptions?: BarSeriesOption;
}

export const PercentageChart: FC<PercentageChartProps> = ({
  chartData,
  onBarOver,
  onBarOut,
  gridOptions,
  barOptions,
}) => {
  const theme = useTheme();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const options: EChartsOption = useMemo(() => {
    const categories = chartData.map(({ name }) => name);
    const maxValue = Math.max(...chartData.map(({ value }) => value));

    const seriesData: SeriesData[] = chartData.map((d) => {
      const barColor = d.itemStyle?.color || theme.palette.primary.main;
      const color = d.value === 0 ? getZeroValueHorizontalBarColor(barColor) : barColor;

      return {
        key: d.key,
        value: d.value * getScaleFactor(maxValue, PERCENTAGE_MAX_VALUE),
        itemStyle: { color },
        emphasis: { itemStyle: { color } },
      };
    });

    return {
      grid: {
        left: 0,
        right: 75,
        top: 0,
        bottom: 0,
        ...gridOptions,
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: PERCENTAGE_MAX_VALUE,
        show: false,
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: categories,
        position: 'right',
        axisLabel: {
          show: true,
          color: theme.palette.text.secondary,
          fontSize: theme.typography.caption.fontSize,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          type: BAR_TYPE,
          data: seriesData,
          label: { show: false },
          itemStyle: { borderRadius: theme.shape.borderRadiusXs },
          ...barOptions,
        },
      ],
    };
  }, [chartData, theme]);

  return (
    <ReactECharts
      option={options}
      style={{ height: '100%', width: '100%' }}
      notMerge={true}
      onEvents={{
        mouseover: (params: { data: SeriesData }) => onBarOver?.(params.data.key),
        mouseout: () => onBarOut?.(),
      }}
    />
  );
};
